// ═══════════════════════════════════════════════════════════════
// ClearView CRM Momentum Ticker — Compact horizontal strip
// Shows members with rising/falling engagement scores
// Click a name → navigates to member detail view
// ═══════════════════════════════════════════════════════════════
import React, { useState, useRef, useEffect, useMemo } from "react";
import { ChevronRight } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";

// ── Time window options ──────────────────────────────────────
const TIME_WINDOWS = [
  { key: "7d", label: "7d", days: 7 },
  { key: "30d", label: "30d", days: 30 },
  { key: "60d", label: "60d", days: 60 },
  { key: "90d", label: "90d", days: 90 },
];

// ── Pathway dot ──────────────────────────────────────────────
const PathwayDot = ({ color }) => (
  <span
    style={{
      width: 7,
      height: 7,
      borderRadius: "50%",
      background: color,
      display: "inline-block",
      flexShrink: 0,
    }}
  />
);

// ── Change arrow + number ────────────────────────────────────
const ChangeIndicator = ({ change }) => {
  const isUp = change > 0;
  return (
    <span
      style={{
        fontSize: 14,
        fontWeight: 700,
        fontFamily: FONT_FAMILY,
        color: isUp ? "#10b981" : "#ef4444",
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexShrink: 0,
      }}
    >
      <span style={{ fontSize: 12 }}>{isUp ? "▲" : "▼"}</span>
      {isUp ? "+" : ""}
      {change.toFixed(1)}
    </span>
  );
};

// ── Calculate engagement score at a point in time ────────────
// Computes what a member's score would be if we only consider
// contact logs before the cutoff date, with decay applied
// relative to that cutoff date
const calculateScoreAtDate = (member, asOfDate, scoringConfig) => {
  const contactLog = member.contactLog || [];
  if (contactLog.length === 0) return 0;

  // Default point values if no scoring config
  const pointValues = scoringConfig?.pointValues || {
    email: 2.5,
    phone: 2.5,
    text: 2.5,
    coffee: 10,
    lunch: 15,
    meeting: 10,
    donation: 20,
    volunteering: 15,
    event: 1.5,
  };

  const decayMonths = scoringConfig?.decayMonths || 6;
  const decayMs = decayMonths * 30 * 24 * 60 * 60 * 1000;

  let score = 0;
  const asOfMs = asOfDate.getTime();

  contactLog.forEach((log) => {
    const logDate = new Date(log.logDate || log.date || log.created_at);
    if (isNaN(logDate.getTime())) return;
    if (logDate.getTime() > asOfMs) return; // Ignore logs after asOfDate

    const type = (log.type || "email").toLowerCase();
    const basePoints = pointValues[type] || 2.5;
    const ageMs = asOfMs - logDate.getTime();

    // Apply decay: full points within decay window, half after
    const points = ageMs > decayMs ? basePoints * 0.5 : basePoints;
    score += points;
  });

  return score;
};

// ── Get pathway color for a member ───────────────────────────
const getPathwayColor = (member, pathways) => {
  const pw = pathways.find((p) => p.id === member.pathway);
  return pw?.color || "#9ca3af";
};

// ═══════════════════════════════════════════════════════════════
// MOMENTUM TICKER COMPONENT
// ═══════════════════════════════════════════════════════════════
export const MomentumTicker = () => {
  const { palette, members, pathways, navigate, scoringConfig } = useApp();
  const [timeWindow, setTimeWindow] = useState("30d");
  const [showArrow, setShowArrow] = useState(false);
  const scrollRef = useRef(null);

  // Check if scrollable content overflows
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const checkOverflow = () => {
      setShowArrow(el.scrollWidth > el.clientWidth + 4);
    };
    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [members, timeWindow]);

  // ── Calculate movers ─────────────────────────────────────
  const movers = useMemo(() => {
    const windowConfig = TIME_WINDOWS.find((tw) => tw.key === timeWindow);
    if (!windowConfig) return [];

    const now = new Date();
    const periodStart = new Date(now);
    periodStart.setDate(periodStart.getDate() - windowConfig.days);

    const results = members
      .map((member) => {
        const currentScore = calculateScoreAtDate(member, now, scoringConfig);
        const pastScore = calculateScoreAtDate(
          member,
          periodStart,
          scoringConfig
        );
        const change = currentScore - pastScore;

        return {
          id: member.id,
          name: `${member.firstName || ""} ${member.lastName || ""}`.trim(),
          change,
          currentScore,
          pathway: member.pathway,
          color: getPathwayColor(member, pathways),
          member,
        };
      })
      .filter((m) => Math.abs(m.change) >= 0.5) // Only show meaningful changes
      .sort((a, b) => Math.abs(b.change) - Math.abs(a.change));

    return results;
  }, [members, pathways, timeWindow, scoringConfig]);

  const handleScrollRight = () => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: 200, behavior: "smooth" });
  };

  const handleMemberClick = (mover) => {
    navigate("memberDetail", mover.member);
  };

  // Don't render if no movers
  if (movers.length === 0) return null;

  const risers = movers.filter((m) => m.change > 0);
  const fallers = movers.filter((m) => m.change < 0);

  return (
    <div
      style={{
        background: palette.cardBg,
        borderRadius: 10,
        border: `1px solid ${palette.cardBorder}`,
        padding: "7px 12px",
        margin: "0 16px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        position: "relative",
        flexShrink: 0,
      }}
    >
      {/* Icon + label */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 13, lineHeight: 1 }}>📊</span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: palette.text2,
            fontFamily: FONT_FAMILY,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          Momentum
        </span>
      </div>

      {/* Time window selector */}
      <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
        {TIME_WINDOWS.map((tw) => (
          <button
            key={tw.key}
            onClick={() => setTimeWindow(tw.key)}
            style={{
              padding: "2px 8px",
              borderRadius: 6,
              border: "none",
              cursor: "pointer",
              fontSize: 10,
              fontWeight: 600,
              fontFamily: FONT_FAMILY,
              background:
                timeWindow === tw.key ? palette.accent1 : "rgba(0,0,0,0.04)",
              color: timeWindow === tw.key ? "#fff" : palette.text3,
              transition: "all 0.2s ease",
            }}
          >
            {tw.label}
          </button>
        ))}
      </div>

      {/* Divider */}
      <div
        style={{
          width: 1,
          height: 18,
          background: palette.cardBorder,
          flexShrink: 0,
        }}
      />

      {/* Scrollable ticker area */}
      <div
        ref={scrollRef}
        style={{
          display: "flex",
          gap: 14,
          overflow: "auto",
          flex: 1,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          maskImage: showArrow
            ? "linear-gradient(to right, black 90%, transparent 100%)"
            : "none",
          WebkitMaskImage: showArrow
            ? "linear-gradient(to right, black 90%, transparent 100%)"
            : "none",
        }}
      >
        {/* Risers first, then fallers */}
        {risers.map((m) => (
          <button
            key={`r-${m.id}`}
            onClick={() => handleMemberClick(m)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              flexShrink: 0,
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "2px 4px",
              borderRadius: 6,
              transition: "background 0.15s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(0,0,0,0.04)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
          >
            <PathwayDot color={m.color} />
            <span
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: palette.text1,
                fontFamily: FONT_FAMILY,
                whiteSpace: "nowrap",
              }}
            >
              {m.name}
            </span>
            <ChangeIndicator change={m.change} />
          </button>
        ))}

        {/* Visual separator between risers and fallers */}
        {risers.length > 0 && fallers.length > 0 && (
          <div
            style={{
              width: 1,
              height: 18,
              background: palette.cardBorder,
              flexShrink: 0,
              alignSelf: "center",
            }}
          />
        )}

        {fallers.map((m) => (
          <button
            key={`f-${m.id}`}
            onClick={() => handleMemberClick(m)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              flexShrink: 0,
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "2px 4px",
              borderRadius: 6,
              transition: "background 0.15s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(0,0,0,0.04)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
          >
            <PathwayDot color={m.color} />
            <span
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: palette.text1,
                fontFamily: FONT_FAMILY,
                whiteSpace: "nowrap",
              }}
            >
              {m.name}
            </span>
            <ChangeIndicator change={m.change} />
          </button>
        ))}
      </div>

      {/* Scroll arrow indicator */}
      {showArrow && (
        <button
          onClick={handleScrollRight}
          style={{
            flexShrink: 0,
            width: 24,
            height: 24,
            borderRadius: 6,
            border: `1px solid ${palette.cardBorder}`,
            background: palette.cardBg,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            transition: "all 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = palette.accent1;
            e.currentTarget.querySelector("svg").style.color = "#fff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = palette.cardBg;
            e.currentTarget.querySelector("svg").style.color = palette.text3;
          }}
        >
          <ChevronRight
            size={ICON_SIZES.sm}
            style={{ color: palette.text3, transition: "color 0.15s ease" }}
          />
        </button>
      )}
    </div>
  );
};
